import { useEffect, useState } from "react";
import { t } from "@/lib/i18n";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import LangToggle from "@/components/LangToggle";

export const NAV = [
  { to: "/", label: "Home" },
  { to: "/scrap-rates", label: "Scrap Rates" },
  { to: "/kabadiwalas", label: "For Kabadiwalas" },
  { to: "/learn", label: "Learn" },
  { to: "/news", label: "News" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

const slug = (s) => s.toLowerCase().replace(/\s+/g, "-");

export default function Header() {
  const { content } = useSite();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`sticky top-0 z-40 transition-colors duration-300 ${scrolled ? "bg-[var(--paper)]/85 backdrop-blur-xl border-b border-[rgba(20,40,26,.08)]" : "bg-transparent"}`}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 sm:h-20 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center shrink-0" data-testid="header-logo-link">
          <img src="/logo-horizontal.png" alt={t("Kindsaver — Kind. Clean. Bold.")} className="h-8 sm:h-10 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-${slug(n.label)}`}
              className={({ isActive }) =>
                `px-3 py-2 rounded-full text-sm font-medium transition-colors ${isActive ? "bg-[var(--leaf-soft)] text-[var(--leaf-dark)]" : "text-[var(--ink-soft)] hover:text-[var(--ink)]"}`
              }
            >
              {t(n.label)}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <LangToggle testid="header-lang-toggle" />
          <a href={content.consumer_app_url} className="btn-leaf !py-2.5 !px-4 text-sm hidden sm:inline-flex" data-testid="header-book-pickup-btn">
            {content.consumer_cta_label} <ArrowUpRight size={16} />
          </a>
          <button
            className="lg:hidden p-2.5 rounded-full border border-[rgba(20,40,26,.12)] bg-white"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[rgba(20,40,26,.08)] bg-[var(--paper)] fade-up" data-testid="mobile-nav">
          <div className="px-4 py-4 flex flex-col gap-1">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={`mobile-nav-${slug(n.label)}`}
                className={({ isActive }) => `px-4 py-3 rounded-xl text-base font-medium ${isActive ? "bg-[var(--leaf-soft)] text-[var(--leaf-dark)]" : "text-[var(--ink)]"}`}>
                {t(n.label)}
              </NavLink>
            ))}
            <div className="grid grid-cols-2 gap-2 mt-3">
              <a href={content.consumer_app_url} className="btn-leaf text-sm" data-testid="mobile-book-pickup-btn">{content.consumer_cta_label}</a>
              <a href={content.partner_app_url} className="btn-ghost text-sm" data-testid="mobile-partner-btn">{content.partner_cta_label}</a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
